import { FunctionComponent } from 'react'
import styled from 'styled-components'

interface ITextProps {
  fontSize: number
  fontTabletSize?: number
  fontDesktopSize?: number
  fontWeight?: number
  mobilePadding?: string
}

const StyledHeader = styled.div<ITextProps>`
  margin: 0;
  padding: ${props => (props.mobilePadding ? props.mobilePadding : '0')};
  font-weight: ${props => props.fontWeight || 400};
  font-size: ${props => props.fontSize}px;
  text-align: center;
  color: ${props => props.theme.colors[props.color || 'black']};

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: ${props => props.fontTabletSize || props.fontSize}px;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: ${props => props.fontDesktopSize || props.fontSize}px;
  }
`

const StyledH1 = styled(StyledHeader).attrs({ as: 'h1' })``
const StyledH2 = styled(StyledHeader).attrs({ as: 'h2' })``
const StyledH3 = styled(StyledHeader).attrs({ as: 'h3' })``

interface IMaxWidthProps {
  maxWidth?: number
  mobileMaxWidth?: number
  tabletMaxWidth?: number
  desktopMaxWidth?: number
}

/*
  Use maxWidth prop to set max-width of the header in all views.
  Otherwise, specify max-width on different views with mobileMaxWidth, tabletMaxWidth, or desktopMaxWidth.
*/
const MaxWidthContainer = styled.div<IMaxWidthProps>`
  margin: 0 auto;
  max-width: ${props => (props.mobileMaxWidth ? `${props.mobileMaxWidth}px` : 'none')};
  ${props => props.maxWidth && `max-width: ${props.maxWidth};`}
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    max-width: ${props => (props.tabletMaxWidth ? `${props.tabletMaxWidth}px` : 'none')};
    ${props => props.maxWidth && `max-width: ${props.maxWidth};`}
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    max-width: ${props => (props.desktopMaxWidth ? `${props.desktopMaxWidth}px` : 'none')};
    ${props => props.maxWidth && `max-width: ${props.maxWidth};`}
  }
`

interface IProps {
  text: string
  headerOne?: boolean
  headerTwo?: boolean
  headerThree?: boolean
  fontSize: number
  fontTabletSize?: number
  fontDesktopSize?: number
  fontWeight?: number
  maxWidth?: number
  mobileMaxWidth?: number
  tabletMaxWidth?: number
  desktopMaxWidth?: number
  mobilePadding?: string
}

const Header: FunctionComponent<IProps> = ({
  text,
  headerOne,
  headerTwo,
  headerThree,
  fontSize,
  fontTabletSize,
  fontDesktopSize,
  fontWeight,
  maxWidth,
  mobileMaxWidth,
  tabletMaxWidth,
  desktopMaxWidth,
  mobilePadding,
}) => {
  // Header is set with div by default.
  // Title can be H1, H2, or H3 for SEO reasons (they all have the same styles).
  let Title = StyledHeader
  if (headerOne) Title = StyledH1

  if (headerTwo) Title = StyledH2

  if (headerThree) Title = StyledH3

  return (
    <MaxWidthContainer
      maxWidth={maxWidth}
      mobileMaxWidth={mobileMaxWidth}
      tabletMaxWidth={tabletMaxWidth}
      desktopMaxWidth={desktopMaxWidth}
    >
      <Title
        fontSize={fontSize}
        fontTabletSize={fontTabletSize}
        fontDesktopSize={fontDesktopSize}
        fontWeight={fontWeight}
        mobilePadding={mobilePadding}
      >
        {text}
      </Title>
    </MaxWidthContainer>
  )
}

export default Header
